import { FEATURED_LECTURERS } from "./featuredLecturers.js";
import { localized } from "../../i18n/content.js";

/**
 * The API has no /lecturers/ endpoint, so the instructor list is aggregated
 * from the lecturers embedded in each course of the /courses/all/ payload.
 */

/**
 * A course is tagged with one root category plus any number of subcategories.
 * Accumulating all of them makes the field line 140–240 characters for a
 * prolific lecturer, and because grid rows equalise height every instructor
 * card inherits the tallest. Only the three roots — which partition the
 * catalogue exactly — belong on the card.
 */
const ROOT_CATEGORY_SLUGS = new Set(["biznis", "tehnologija", "kreativa"]);

/**
 * Bosnian names reach us in inconsistent Unicode forms: the API returns some
 * lecturers with a decomposed "ć" (c + U+0301) while anything typed by hand
 * produces the precomposed U+0107. The two render identically but are not
 * equal strings, so a hand-picked name would silently match nothing.
 */
const key = (name) => (name ?? "").normalize("NFC");

/**
 * A featured entry is either a bare name or `{ name, role, focus }`. The string
 * form is what the list used to be and what most of the tests still pass, so it
 * stays supported rather than forcing every caller to wrap its names in objects.
 */
const asEntry = (entry) =>
	typeof entry === "string" ? { name: entry } : (entry ?? {});

export function deriveInstructors(
	courses,
	limit = 5,
	featured = FEATURED_LECTURERS,
	language = "bs"
) {
	const byName = new Map();

	for (const course of courses ?? []) {
		for (const lecturer of course?.lecturers ?? []) {
			if (!lecturer?.name) continue;

			const entry = byName.get(key(lecturer.name)) ?? {
				name: lecturer.name,
				pictureUrl: lecturer.picture_url ?? null,
				courseCount: 0,
				// Keyed by slug so the names can be ordered by the counts
				// below, which are also keyed by slug.
				categories: new Map(),
				// Counted, not collected: the card is patterned after one
				// discipline, and someone who teaches four business courses and
				// one technology course belongs to business.
				categoryCounts: new Map(),
			};

			entry.courseCount += 1;
			if (!entry.pictureUrl && lecturer.picture_url) {
				entry.pictureUrl = lecturer.picture_url;
			}
			for (const category of course?.categories ?? []) {
				if (!category?.name) continue;
				if (!ROOT_CATEGORY_SLUGS.has(category.slug)) continue;
				entry.categories.set(category.slug, localized(category, "name", language));
				entry.categoryCounts.set(
					category.slug,
					(entry.categoryCounts.get(category.slug) ?? 0) + 1
				);
			}

			byName.set(key(lecturer.name), entry);
		}
	}

	// Ties keep insertion order, so a lecturer split evenly between two
	// disciplines takes the one their first course was filed under.
	const dominant = (counts) => {
		let best = null;
		for (const [slug, count] of counts) {
			if (best === null || count > counts.get(best)) best = slug;
		}
		return best;
	};

	// Most-taught discipline first. An economist with one technology course
	// should not lead with Tehnologija just because that course happens to sit
	// earlier in the catalogue. The sort is stable, so an even split still
	// falls back to the order the courses were read in.
	const ordered = (categories, counts) =>
		[...categories.entries()]
			.sort(
				([a], [b]) => (counts.get(b) ?? 0) - (counts.get(a) ?? 0)
			)
			.map(([, name]) => name);

	// `role` and `focus` are editorial, so they only ever arrive from the
	// hand-picked list. The count-ranked path leaves them null and the card
	// falls back to the category line.
	const toCard = ({ categories, categoryCounts, ...rest }) => {
		// Both forms, because they are read differently: `field` is the
		// one-line fallback for a lecturer with no hand-written role, and
		// `categories` is the list the card renders as a chip apiece.
		const names = ordered(categories, categoryCounts);

		return {
			role: null,
			focus: null,
			...rest,
			categories: names,
			field: names.join(" · "),
			category: dominant(categoryCounts),
		};
	};

	// A hand-picked list wins: this section is editorial, and ranking by course
	// count skewed it almost entirely to Biznis while leaving the fifth slot to
	// a four-way tie broken by catalogue order.
	//
	// A name that matches nothing is skipped rather than substituted, so a typo
	// shows up as a missing card instead of silently promoting someone else.
	if (featured?.length) {
		return featured
			.map((entry) => {
				const { name, role, focus } = asEntry(entry);
				const found = byName.get(key(name));
				// Spread rather than mutate: `found` is the aggregate shared
				// with the ranked path below. Coalesced rather than passed
				// straight through, because a spread carries an undefined value
				// over toCard's null default and the card would then see
				// `undefined` for a bare-name entry.
				return found
					? { ...found, role: role ?? null, focus: focus ?? null }
					: null;
			})
			.filter(Boolean)
			.slice(0, limit)
			.map(toCard);
	}

	return [...byName.values()]
		.sort((a, b) => b.courseCount - a.courseCount)
		.slice(0, limit)
		.map(toCard);
}
